import { useTheme } from '../App';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const sections = [
  { id: 'introduction', title: '1. Introduction' },
  { id: 'eligibility', title: '2. Eligibility' },
  { id: 'service-description', title: '3. Service Description' },
  { id: 'user-accounts', title: '4. User Accounts' },
  { id: 'payment-terms', title: '5. Payment Terms' },
  { id: 'appeal-process', title: '6. Appeal Process' },
  { id: 'intellectual-property', title: '7. Intellectual Property' },
  { id: 'limitation-of-liability', title: '8. Limitation of Liability' },
  { id: 'termination', title: '9. Termination' },
  { id: 'governing-law', title: '10. Governing Law' },
  { id: 'changes', title: '11. Changes to Terms' },
  { id: 'contact', title: '12. Contact Information' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.04, duration: 0.5, ease: 'easeOut' } }),
};

export default function TermsPage() {
  const { theme } = useTheme();
  const isDark = theme !== 'aurora';
  const { t } = useTranslation();

  const card = isDark
    ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border border-white/[0.05]'
    : 'bg-white border border-gray-200 shadow-sm';
  const heading = isDark ? 'text-white' : 'text-gray-900';
  const body = isDark ? 'text-white/60' : 'text-gray-600';
  const bodyStrong = isDark ? 'text-white/80' : 'text-gray-700';

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={`min-h-screen transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
      {isDark && (
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[120px]" />
        </div>
      )}

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-24 sm:py-32">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              Terms of Service
            </span>
          </h1>
          <p className={`text-lg ${body}`}>
            Please read these terms carefully before using the myAct platform.
          </p>
          <p className={`text-sm mt-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
            Last updated: March 1, 2026
          </p>
        </motion.div>

        {/* Table of Contents */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className={`rounded-2xl p-6 sm:p-8 mb-10 ${card}`}
        >
          <h2 className={`font-['Outfit'] text-xl font-semibold mb-4 ${heading}`}>Table of Contents</h2>
          <nav className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`text-left text-sm px-3 py-2 rounded-lg transition-colors ${
                  isDark
                    ? 'text-white/60 hover:text-white hover:bg-white/[0.05]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {s.title}
              </button>
            ))}
          </nav>
        </motion.div>

        {/* Sections */}
        <div className="space-y-8">
          <motion.div id="introduction" custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>1. Introduction</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>Welcome to myAct ("Platform", "Service", "we", "us", or "our"), operated by Arbbio ("Company"). These Terms of Service ("Terms") govern your access to and use of the myAct platform, including our website at arbbio.com, mobile applications, and all associated services.</p>
              <p>By creating an account, accessing, or using our Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you must not use the Service.</p>
              <p>myAct provides a digital platform that facilitates the submission, management, and tracking of appeals under the European Union's Digital Services Act (DSA) Regulation (EU) 2022/2065. Our Service acts as an intermediary to help users exercise their rights under the DSA framework.</p>
            </div>
          </motion.div>

          <motion.div id="eligibility" custom={1} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>2. Eligibility</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>To use the myAct platform, you must:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Be at least 18 years of age, or the age of legal majority in your jurisdiction</li>
                <li>Have the legal capacity to enter into a binding agreement</li>
                <li>Not be prohibited from using the Service under applicable laws</li>
                <li>Be a resident of the European Union, European Economic Area (EEA), or a jurisdiction where DSA rights apply</li>
                <li>Provide accurate and complete registration information</li>
              </ul>
              <p>If you are using the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.</p>
            </div>
          </motion.div>

          <motion.div id="service-description" custom={2} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>3. Service Description</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>myAct provides the following services:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><span className={bodyStrong}>Appeal Submission:</span> A structured process to file appeals against content moderation decisions made by online platforms subject to the DSA</li>
                <li><span className={bodyStrong}>Case Management:</span> Tools to manage, track, and monitor the progress of submitted appeals</li>
                <li><span className={bodyStrong}>Document Preparation:</span> Assistance in preparing legally compliant appeal documentation in accordance with DSA requirements</li>
                <li><span className={bodyStrong}>Platform Communication:</span> Facilitation of communication between users and digital platforms regarding appeal outcomes</li>
                <li><span className={bodyStrong}>Status Tracking:</span> Real-time updates and notifications regarding the status of your appeals</li>
              </ul>
              <p>We do not provide legal advice. Our Service facilitates the administrative process of filing appeals. For legal counsel, we recommend consulting a qualified legal professional in your jurisdiction.</p>
            </div>
          </motion.div>

          <motion.div id="user-accounts" custom={3} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>4. User Accounts</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>When you create an account with us, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information to keep it accurate</li>
                <li>Maintain the security and confidentiality of your login credentials</li>
                <li>Accept responsibility for all activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
              <p>We reserve the right to suspend or terminate accounts that violate these Terms, provide false information, or engage in fraudulent activity. You may not transfer your account to another person without our prior written consent.</p>
            </div>
          </motion.div>

          <motion.div id="payment-terms" custom={4} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>5. Payment Terms</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>Certain features of the Service require payment. By selecting a paid plan or service, you agree to the following:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><span className={bodyStrong}>Pricing:</span> All prices are displayed in Euros and include applicable VAT where required by law</li>
                <li><span className={bodyStrong}>Billing:</span> Payments are processed through secure third-party payment processors. We do not store your full payment card details</li>
                <li><span className={bodyStrong}>Subscriptions:</span> Subscription plans renew automatically unless cancelled before the renewal date. You may cancel at any time through your account settings</li>
                <li><span className={bodyStrong}>Refunds:</span> Refund requests are handled in accordance with EU consumer protection laws. You have a 14-day cooling-off period from the date of purchase for subscription services, unless the service has already been fully performed</li>
                <li><span className={bodyStrong}>Failed Payments:</span> If a payment fails, we may suspend access to paid features until the payment is successfully processed</li>
              </ul>
              <p>We reserve the right to modify our pricing with 30 days' advance notice. Existing subscriptions will honour the pricing at the time of their current billing cycle.</p>
            </div>
          </motion.div>

          <motion.div id="appeal-process" custom={5} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>6. Appeal Process</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>When submitting an appeal through myAct, you acknowledge and agree that:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>All information provided in your appeal must be truthful, accurate, and complete to the best of your knowledge</li>
                <li>You are the rightful owner or authorized representative of the account or content subject to the appeal</li>
                <li>The outcome of any appeal is ultimately determined by the relevant platform or out-of-court dispute settlement body, not by myAct</li>
                <li>We do not guarantee any specific outcome for appeals filed through our platform</li>
                <li>Processing times for appeals vary depending on the platform and the complexity of the case</li>
                <li>You authorize myAct to act on your behalf in communicating with platforms and relevant authorities as necessary to process your appeal</li>
              </ul>
              <p>We will make reasonable efforts to process your appeal promptly and in accordance with the timelines established under the DSA. However, we cannot control the response times or decisions of third-party platforms.</p>
            </div>
          </motion.div>

          <motion.div id="intellectual-property" custom={6} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>7. Intellectual Property</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>The myAct platform, including its design, source code, features, content, trademarks, logos, and all related intellectual property, is owned by Arbbio and protected by applicable intellectual property laws.</p>
              <p>You are granted a limited, non-exclusive, non-transferable, revocable licence to use the Service for its intended purpose. You may not:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Copy, modify, distribute, or create derivative works based on the Service</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                <li>Remove or alter any proprietary notices or labels on the Service</li>
                <li>Use our trademarks or branding without prior written consent</li>
              </ul>
              <p>Content you submit through the Service remains your intellectual property. By submitting content, you grant myAct a limited licence to use, process, and transmit that content solely for the purpose of providing the Service.</p>
            </div>
          </motion.div>

          <motion.div id="limitation-of-liability" custom={7} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>8. Limitation of Liability</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>To the maximum extent permitted by applicable law:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>myAct provides the Service "as is" and "as available" without warranties of any kind, whether express or implied</li>
                <li>We do not warrant that the Service will be uninterrupted, error-free, or secure at all times</li>
                <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service</li>
                <li>Our total aggregate liability to you for any claims arising from these Terms or the Service shall not exceed the amount you paid to us in the twelve (12) months preceding the claim</li>
              </ul>
              <p>Nothing in these Terms shall limit or exclude our liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be limited or excluded under applicable EU or member state law.</p>
            </div>
          </motion.div>

          <motion.div id="termination" custom={8} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>9. Termination</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>Either party may terminate this agreement as follows:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><span className={bodyStrong}>By You:</span> You may close your account at any time through your account settings or by contacting our support team. Upon closure, any active subscriptions will be cancelled at the end of the current billing period</li>
                <li><span className={bodyStrong}>By Us:</span> We may suspend or terminate your access to the Service immediately if you breach these Terms, engage in fraudulent activity, or if required by law</li>
              </ul>
              <p>Upon termination, your right to use the Service ceases immediately. We will retain your data in accordance with our Privacy Policy and applicable data retention laws. You may request a copy of your personal data before account closure in accordance with your GDPR rights.</p>
            </div>
          </motion.div>

          <motion.div id="governing-law" custom={9} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>10. Governing Law</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>These Terms shall be governed by and construed in accordance with the laws of the European Union and the applicable laws of the member state in which the Company is established.</p>
              <p>Any disputes arising from or in connection with these Terms shall be subject to the exclusive jurisdiction of the competent courts in the Company's place of establishment, without prejudice to any mandatory consumer protection provisions that grant you the right to bring proceedings in the courts of your country of residence.</p>
              <p>You retain all rights granted to you under applicable EU consumer protection regulations, including Directive 2011/83/EU on consumer rights and Regulation (EU) 2022/2065 (the Digital Services Act).</p>
            </div>
          </motion.div>

          <motion.div id="changes" custom={10} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>11. Changes to Terms</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>We reserve the right to modify these Terms at any time. When we make material changes, we will:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Provide at least 30 days' notice before changes take effect</li>
                <li>Notify you via email and/or a prominent notice on our platform</li>
                <li>Update the "Last Updated" date at the top of these Terms</li>
              </ul>
              <p>Your continued use of the Service after the effective date of any changes constitutes your acceptance of the updated Terms. If you do not agree with the changes, you must stop using the Service and close your account.</p>
            </div>
          </motion.div>

          <motion.div id="contact" custom={11} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`rounded-2xl p-6 sm:p-8 ${card}`}>
            <h2 className={`font-['Outfit'] text-2xl font-semibold mb-4 ${heading}`}>12. Contact Information</h2>
            <div className={`space-y-4 text-sm leading-relaxed ${body}`}>
              <p>If you have any questions, concerns, or requests regarding these Terms of Service, please contact us:</p>
              <div className={`rounded-xl p-5 mt-4 ${isDark ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                <p className={`font-medium ${bodyStrong}`}>Arbbio (myAct)</p>
                <p className="mt-2">Email: legal@arbbio.com</p>
                <p>Support: support@arbbio.com</p>
                <p>Website: https://arbbio.com</p>
              </div>
              <p className="mt-4">For urgent matters related to ongoing appeals, please use the in-platform messaging system for the fastest response.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
